import React from "react";
import { Skeleton, Space } from "antd";
import { VolumeUp, Calendar2 } from "@styled-icons/bootstrap";
import { generatePath, useNavigate } from "react-router-dom";

import { useGetUserPreview } from "API/queries/userApiQueries";
import { StudentInviteBox } from "../../StudentInviteBox";
import { useTranslation } from "react-i18next";
import { TLang } from "../../../assets/data/isoNameLanguage";
import { getLanguage } from "../../../utils/fetchMapInfo";
import { IHouse, IMatch, IPerson } from "../../../interfaces/interfaces";
import { useContextHospi } from "../../../context/ContextHospi";
import { IChat } from "../../../interfaces/interfacesChat";
import { formatDateToShort, parseServerDateOrTime } from "../../../utils/time";

import {
  Root,
  Heading,
  Chevron,
  FeaturesBlock,
  FeaturesIcon,
  FeaturesValue,
  LinkButton,
  LinkButtonsContainer,
  LinkButtonText,
  ProfileButtonText,
  ProfileButton,
  ProfilePicture,
  Delimiter
} from "./styles";
import { STUDENT_ROUTE_ID } from "../../../utils/constants";

export const formatPreferredDate = (date?: string | null) => {
  return date ? formatDateToShort(parseServerDateOrTime(date)) : "Flexible";
};

export const StudentProfileChatBox = React.memo<{
  house: IHouse;
  match: IMatch;
  chat: IChat;
}>(({ chat, house, match }) => {
  const { userProfile } = useContextHospi();
  const { data: user, isLoading } = useGetUserPreview(match.student_id);
  const { i18n, t } = useTranslation("hostchatbox");
  const navigate = useNavigate();

  const currentLanguage: TLang = (i18n.language.toUpperCase() as TLang) ?? "GB";
  const allLanguages = getLanguage(currentLanguage);

  const spokenLanguagesLabels = user?.spoken_languages?.map(
    (languageCode) =>
      allLanguages.find((l) => l.value === languageCode)?.label ?? languageCode
  );

  return (
    <Root>
      {isLoading ? (
        <Space style={{ width: "100%" }} direction="vertical" size="large">
          <Skeleton avatar active />
          <Skeleton avatar={false} active />
          <Skeleton avatar={false} active />
        </Space>
      ) : null}
      {user ? (
        <>
          <ProfilePicture src={user.thumbnail_url} />
          <Heading>
            {user.first_name} {user.last_name}
          </Heading>
          <FeaturesBlock>
            <FeaturesIcon>
              <VolumeUp />
            </FeaturesIcon>
            <FeaturesValue>{spokenLanguagesLabels?.join(", ")}</FeaturesValue>
          </FeaturesBlock>
          <FeaturesBlock>
            <FeaturesIcon>
              <Calendar2 />
            </FeaturesIcon>
            <FeaturesValue>
              {t(`roomsDetails.preferredDate`)}
              <div>
                {formatPreferredDate(user.preferred_move_in)} -{" "}
                {formatPreferredDate(user.preferred_move_out)}
              </div>
            </FeaturesValue>
          </FeaturesBlock>
          <ProfileButton
            loading={false}
            type="text"
            onClick={() => {
              navigate(
                generatePath(STUDENT_ROUTE_ID, {
                  lng: i18n.language,
                  id: match.student_id
                })
              );
            }}
          >
            <ProfileButtonText>
              {t(`roomsDetails.viewProfile`)}
            </ProfileButtonText>
            <Chevron />
          </ProfileButton>
          {match.match_status !== undefined ? (
            <StudentInviteBox
              host={userProfile as IPerson}
              student={user as IPerson}
              house={house}
              match={match}
              additionalInfo={<>{t(`roomsDetails.calculatePrice`)}</>}
              allowOnlyCancel={chat.removed_by_host}
            />
          ) : null}
          <Delimiter />
          <Heading>{t(`roomsDetails.tipsAndTricks`)}</Heading>
          <LinkButtonsContainer>
            <LinkButton
              type="text"
              href={`/${i18n.language}/blog/verbeteringen-website-handleiding-voor-hosts`}
              target={"_blank"}
            >
              <LinkButtonText>
                {t(`roomsDetails.matchmakingWork`)}
              </LinkButtonText>
              <Chevron />
            </LinkButton>
            <LinkButton
              type="text"
              href={`/${i18n.language}/blog/vijf-tips-voor-goede-fotos-in-je-kameradvertentie`}
              target={"_blank"}
            >
              <LinkButtonText>{t(`roomsDetails.improveAd`)}</LinkButtonText>
              <Chevron />
            </LinkButton>
          </LinkButtonsContainer>
          <Delimiter />
          <Heading>{t(`roomsDetails.help`)}</Heading>
          <LinkButtonsContainer>
            <LinkButton
              type="text"
              href={`/${i18n.language}/contact`}
              target={"_blank"}
            >
              <LinkButtonText>{t(`roomsDetails.contactHospi`)}</LinkButtonText>
              <Chevron />
            </LinkButton>
          </LinkButtonsContainer>
        </>
      ) : null}
      <div style={{ height: "32px" }} />
    </Root>
  );
});
